'use client';

// Écran de connexion (Option A cookies). Le login établit la session côté serveur ;
// on redirige ensuite vers la bibliothèque.

import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Text } from '@/components/ui/Text';
import { useT } from '@/hooks/useT';
import { authErrorKey } from '@/lib/authError';
import { safeNext } from '@/lib/safeNext';
import { useLogin } from '@/queries/auth';
import { useAuthFlow } from '@/stores/authFlow';
import { fontStyle } from '@/theme/typography';

interface FormValues {
  email: string;
  password: string;
}

// useSearchParams impose une frontière Suspense en App Router.
export default function SignInPage() {
  return (
    <Suspense fallback={null}>
      <SignInForm />
    </Suspense>
  );
}

function SignInForm() {
  const { t } = useT('auth');
  const router = useRouter();
  const searchParams = useSearchParams();
  // Reprise post-login : ?next=/share/xxx renvoie l'utilisateur là où il allait.
  const next = safeNext(searchParams.get('next'));
  const login = useLogin();
  const [formError, setFormError] = useState<string | null>(null);
  const flash = useAuthFlow((s) => s.flash);
  const setFlash = useAuthFlow((s) => s.setFlash);
  // Capture le message à l'affichage (init synchrone), puis on vide le store.
  const [flashMsg] = useState(() => (flash ? t(flash) : null));

  useEffect(() => {
    if (flash) setFlash(null);
  }, [flash, setFlash]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login.mutateAsync(values);
      router.replace(next);
    } catch (e) {
      setFormError(t(`errors.${authErrorKey(e)}`));
    }
  });

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <Text as="h1" variant="displayXl" className="font-display">
          {t('signIn.title')}
        </Text>
        <Text variant="bodyLg" className="text-text-secondary">
          {t('signIn.subtitle')}
        </Text>
      </header>

      {flashMsg ? (
        <Text
          variant="bodySm"
          className="rounded-md border border-success/30 bg-success/10 px-3 py-2 text-success"
          role="status"
        >
          {flashMsg}
        </Text>
      ) : null}

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label={t('signIn.email')}
          type="email"
          autoComplete="email"
          placeholder={t('signIn.emailPlaceholder')}
          error={errors.email ? t(`validation.${errors.email.type === 'pattern' ? 'emailInvalid' : 'emailRequired'}`) : undefined}
          {...register('email', { required: true, pattern: /.+@.+\..+/ })}
        />
        <Input
          label={t('signIn.password')}
          type="password"
          autoComplete="current-password"
          error={errors.password ? t('validation.passwordRequired') : undefined}
          {...register('password', { required: true })}
        />

        {formError ? (
          <Text variant="bodySm" className="text-danger" role="alert">
            {formError}
          </Text>
        ) : null}

        <Button type="submit" fullWidth loading={login.isPending}>
          {t('signIn.submit')}
        </Button>
      </form>

      <SocialAuthButtons />

      <div className="flex flex-col items-center gap-3">
        <Link href="/forgot-password" className="text-action hover:underline" style={fontStyle('bodySm')}>
          {t('signIn.forgot')}
        </Link>
        <Text variant="bodySm" className="text-text-tertiary">
          {t('signIn.noAccount')}{' '}
          <Link href="/sign-up" className="text-action hover:underline">
            {t('signIn.createAccount')}
          </Link>
        </Text>
      </div>
    </div>
  );
}
